import React, { useState, useEffect } from 'react';
import * as api from '../services/apiService';
import { useUI } from '../contexts/UIProvider';
import { useAuth } from '../contexts/AuthProvider';
import type { SavedFlyer } from '../types';
import PhotoIcon from './icons/PhotoIcon';
import TrashIcon from './icons/TrashIcon';
import CloseIcon from './icons/CloseIcon';

const MyFlyersPage: React.FC = () => {
    const { currentUser } = useAuth();
    const [flyers, setFlyers] = useState<SavedFlyer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [viewingFlyer, setViewingFlyer] = useState<SavedFlyer | null>(null);
    const { showToast } = useUI();

    useEffect(() => {
        const fetchFlyers = async () => {
            try {
                setLoading(true);
                const data = await api.getSavedFlyers();
                setFlyers(data);
            } catch (err) {
                const message = err instanceof Error ? err.message : 'An unknown error occurred.';
                setError(message);
            } finally {
                setLoading(false);
            }
        };
        fetchFlyers();
    }, []);

    const handleDelete = async (e: React.MouseEvent, flyerId: string) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this flyer?')) {
            try {
                await api.deleteSavedFlyer(flyerId);
                setFlyers(prev => prev.filter(f => f.id !== flyerId));
                showToast('Flyer deleted successfully.');
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to delete flyer.';
                showToast(`Error: ${message}`);
            }
        }
    };
    
    const handleDownload = (flyer: SavedFlyer, page: 'graphic' | 'lineup') => {
        const imageUrl = page === 'graphic' ? flyer.config?.graphicBackground : flyer.config?.lineupBackground;
        if (!imageUrl) {
            showToast(`This flyer does not have a ${page} page to download.`);
            return;
        }
        const link = document.createElement('a');
        link.download = `${flyer.opportunityTitle.replace(/ /g, '_')}-${page}-flyer.png`;
        link.href = imageUrl;
        link.click();
    };

    return (
        <div className="space-y-6">
            <div className="bg-brand-surface rounded-lg p-6">
                <div className="flex items-center space-x-3">
                    <PhotoIcon className="h-7 w-7 text-brand-orange" />
                    <h2 className="text-3xl font-bold text-white">My Saved Flyers</h2>
                </div>
                <p className="mt-2 text-brand-muted">A gallery of promotional flyers for your events. Click on a flyer to see both pages.</p>
            </div>
            
            <div className="bg-brand-surface rounded-lg p-6">
                {loading && <p className="text-brand-muted">Loading your saved flyers...</p>}
                {error && <p className="text-red-400">Error: {error}</p>}
                {!loading && !error && (
                    flyers.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {flyers.map(flyer => {
                                const imageUrl = flyer.config?.graphicBackground || flyer.config?.lineupBackground;
                                return (
                                    <div key={flyer.id} className="group relative cursor-pointer" onClick={() => setViewingFlyer(flyer)}>
                                        {imageUrl ? (
                                            <img src={imageUrl} alt={flyer.opportunityTitle} className="w-full h-auto rounded-lg shadow-md object-contain bg-black/20 aspect-[9/16]" />
                                        ) : (
                                            <div className="w-full h-auto rounded-lg shadow-md object-contain bg-black/20 aspect-[9/16] flex items-center justify-center">
                                                <p className="text-center text-brand-muted p-4">
                                                    <span className="font-bold text-white block">Image not available</span>
                                                    {flyer.opportunityTitle}
                                                </p>
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-4 rounded-lg">
                                            <div>
                                                <p className="font-bold text-white">{flyer.opportunityTitle}</p>
                                                <p className="text-xs text-gray-300">Saved: {new Date(flyer.createdAt).toLocaleDateString()}</p>
                                            </div>
                                            {flyer.userId === currentUser?.handle && (
                                                <button onClick={(e) => handleDelete(e, flyer.id)} className="self-end bg-red-600/80 hover:bg-red-500 text-white p-2 rounded-full" aria-label="Delete flyer">
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-center text-brand-muted py-8">You haven't saved any flyers yet. Go to 'My Lineups' to create one!</p>
                    )
                )}
            </div>
            
            {viewingFlyer && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setViewingFlyer(null)}>
                    <div className="bg-brand-surface p-4 rounded-lg shadow-2xl relative" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setViewingFlyer(null)} className="absolute -top-3 -right-3 bg-gray-700 rounded-full p-1 z-10"><CloseIcon className="h-5 w-5"/></button>
                        <div className="flex gap-4 max-h-[85vh]">
                           {viewingFlyer.config?.graphicBackground && (
                                <div className="flex flex-col gap-2 items-center">
                                    <img src={viewingFlyer.config.graphicBackground} alt="Graphic Page" className="object-contain rounded-lg h-[60vh]"/>
                                    <button onClick={() => handleDownload(viewingFlyer, 'graphic')} className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-full text-sm">Download Graphic</button>
                                </div>
                           )}
                           {viewingFlyer.config?.lineupBackground && (
                                <div className="flex flex-col gap-2 items-center">
                                    <img src={viewingFlyer.config.lineupBackground} alt="Lineup Page" className="object-contain rounded-lg h-[60vh]"/>
                                    <button onClick={() => handleDownload(viewingFlyer, 'lineup')} className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-full text-sm">Download Lineup</button>
                                </div>
                           )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyFlyersPage;